import type { ReactNode } from "react";
import { Size } from "@/shared/components/generic/UiEnums.ts";
import "./ScheduleToobarButtons.scss";
import SchedulePaletteOpener from "@/pages/human-resources/time-management/components/schedule-palette/popup/SchedulePaletteOpener.tsx";
import ScheduleAlertsOpener from "@/pages/human-resources/time-management/components/schedule-alerts/ScheduleAlertsOpener.tsx";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";
import ScheduleCancellationOpener from "@/pages/human-resources/time-management/components/schedule-cancellation/ScheduleCancellationOpener.tsx";
import ScheduleValidationButton from "@/pages/human-resources/time-management/components/schedule-validation/ScheduleValidationButton.tsx";
import { filterDraftSchedules } from "@/pages/human-resources/time-management/model/schedule-view/useVisibleDraftSchedules.ts";
import DisplayOptionsButton from "@/pages/human-resources/time-management/schedules/site-schedule/toolbar/display-options/DisplayOptionsButton.tsx";
import ScheduleImportDayOpener from "../../day-view/import-dialog/ScheduleImportDayOpener.tsx";
import CalendarEventsOpener from "@/pages/human-resources/time-management/components/calendar-events/CalendarEventsOpener.tsx";
import DailySchedulePrintMenu from "@/pages/human-resources/time-management/schedules/site-schedule/day-view/print-menu/DailySchedulePrintMenu.tsx";
import useVisibleEmployeesSchedules from "@/pages/human-resources/time-management/model/schedule-view/useVisibleEmployeesSchedules.ts";
import useSchedulesManager from "@/pages/human-resources/time-management/schedules/site-schedule/model/schedule-data/useSchedulesManager.ts";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import { dateHasTimeChange } from "@/shared/utils/Dates.ts";
import IconCalendarStar from "@/shared/components/generic/icons/IconCalendarStar.tsx";
import { useReadCalendarDays } from "@/pages/human-resources/time-management/services/hooks/useReadCalendarDays.ts";
import useScheduleRangeOnRoute from "@/pages/human-resources/time-management/hooks/useScheduleRangeOnRoute.ts";
import type { ScheduleRange } from "@/pages/human-resources/time-management/model/ScheduleRange.ts";
import ScheduleImportMonthOpener from "@/pages/human-resources/time-management/schedules/site-schedule/month-view/import-dialog/ScheduleImportMonthOpener.tsx";
import MonthlySchedulePrintMenu from "@/pages/human-resources/time-management/schedules/site-schedule/month-view/print-menu/MonthlySchedulePrintMenu.tsx";

interface Properties {
  showEvents: boolean;
}

export default function ScheduleToolbarButtons(props: Properties): ReactNode {
  const {
    contractsManager: { siteId, startDate: date },
  } = useSchedulesManager();
  const {
    siteData: {
      siteAddress: { timeZone },
    },
  } = useScheduleReferenceData();
  const timeChange = dateHasTimeChange(date, timeZone);
  const calendarDay = (useReadCalendarDays(siteId, date, date) ?? []).at(0);
  const range = useScheduleRangeOnRoute();

  const { canEditSchedule, canViewDraft } = useScheduleUserPermissions();
  const visibleSchedules = useVisibleEmployeesSchedules();
  const visibleDrafts = filterDraftSchedules(visibleSchedules);

  const size = Size.LARGE;
  return (
    <div className="schedule-toolbar-buttons">
      {props.showEvents && calendarDay !== undefined && (
        <CalendarEventsOpener
          buttonSize={size}
          calendarDay={calendarDay}
          timeChange={timeChange}
          icon={IconCalendarStar}
        />
      )}
      {visibleSchedules.length > 0 && (
        <>
          <SchedulePaletteOpener
            buttonSize={size}
            disabled={!canEditSchedule}
          />
          <ScheduleAlertsOpener buttonSize={size} disabled={!canViewDraft} />
          {renderImportOpenerForRange(range)}
          <ScheduleValidationButton />
          <ScheduleCancellationOpener
            buttonSize={size}
            disabled={
              !canEditSchedule || !canViewDraft || visibleDrafts.length === 0
            }
          />
          {range === "DAY" ?
            <DailySchedulePrintMenu size={size} />
          : <MonthlySchedulePrintMenu size={size} />}
        </>
      )}
      <DisplayOptionsButton size={size} />
    </div>
  );

  function renderImportOpenerForRange(range: ScheduleRange): ReactNode {
    switch (range) {
      case "DAY":
        return (
          <ScheduleImportDayOpener
            buttonSize={size}
            disabled={!canEditSchedule}
          />
        );
      case "MONTH":
        return (
          <ScheduleImportMonthOpener
            buttonSize={size}
            disabled={!canEditSchedule}
          />
        );
      case "WEEK":
        return undefined; // Not available yet
    }
  }
}
